/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import PropTypes from "prop-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  getTariffs,
  getTariffsProperties,
  createBillConfig,
  updateBillConfig,
  deleteBillConfig,
  getArcadiaCalendarsQuery,
  getArcadiaCalendarDetailQuery,
  getArcadiaCalendarDatesQuery,
  upsertSiteIntegration,
  deleteSiteIntegration,
} from "../../lib/Queries";
import PropertyPickerDialog from "../Tariff/PropertyPickerDialog";
import TariffCard from "../Tariff/TariffCard";
import BillingPeriodsCard from "./BillingPeriodsCard";

const ManageArcadiaIntegrationModal = ({
  open,
  onClose,
  currentSite,
  billConfigs = [],
  siteIntegrations = [],
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Pre-project tariff state
  const [preMasterTariffId, setPreMasterTariffId] = useState("");
  const [preTariffResults, setPreTariffResults] = useState([]);
  const [preTariffLoading, setPreTariffLoading] = useState(false);
  const [preSelectedOverrides, setPreSelectedOverrides] = useState([]);
  const [prePropertiesCache, setPrePropertiesCache] = useState({});
  const [preTotalPropertiesCount, setPreTotalPropertiesCount] = useState(0);
  const [prePropertiesLoading, setPrePropertiesLoading] = useState(false);
  const [showPrePropertyPicker, setShowPrePropertyPicker] = useState(false);
  const [prePropertyOptions, setPrePropertyOptions] = useState([]);

  // Post-project tariff state
  const [postTariffSame, setPostTariffSame] = useState(true);
  const [postMasterTariffId, setPostMasterTariffId] = useState("");
  const [postTariffResults, setPostTariffResults] = useState([]);
  const [postTariffLoading, setPostTariffLoading] = useState(false);
  const [postSelectedOverrides, setPostSelectedOverrides] = useState([]);
  const [postPropertiesCache, setPostPropertiesCache] = useState({});
  const [postTotalPropertiesCount, setPostTotalPropertiesCount] = useState(0);
  const [postPropertiesLoading, setPostPropertiesLoading] = useState(false);
  const [showPostPropertyPicker, setShowPostPropertyPicker] = useState(false);
  const [postPropertyOptions, setPostPropertyOptions] = useState([]);

  // Billing periods state
  const [selectedCalendarId, setSelectedCalendarId] = useState("");

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const preConfig = useMemo(
    () =>
      Array.isArray(billConfigs)
        ? billConfigs.find((c) => c.bill_type === "pre_microgrid" && c.active)
        : null,
    [billConfigs],
  );
  const postConfig = useMemo(
    () =>
      Array.isArray(billConfigs)
        ? billConfigs.find((c) => c.bill_type === "post_microgrid" && c.active)
        : null,
    [billConfigs],
  );

  const arcadiaIntegration = useMemo(
    () =>
      Array.isArray(siteIntegrations)
        ? siteIntegrations.find((i) => i.provider === "arcadia")
        : null,
    [siteIntegrations],
  );

  const lseId = useMemo(() => {
    if (preConfig?.lse_id) return preConfig.lse_id;
    if (postConfig?.lse_id) return postConfig.lse_id;
    const findActive = (results) =>
      results.find((t) => t?.endDate == null) || results[results.length - 1] || null;
    return findActive(preTariffResults)?.lseId || findActive(postTariffResults)?.lseId || null;
  }, [preConfig, postConfig, preTariffResults, postTariffResults]);

  const { data: calendarsData } = useQuery({
    ...getArcadiaCalendarsQuery(lseId),
    enabled: !!lseId && open,
  });

  const calendars = calendarsData?.results || [];

  const effectiveCalendarId = selectedCalendarId || arcadiaIntegration?.config?.calendar_id || "";

  const { data: calendarDetailData, isLoading: calendarDetailLoading } = useQuery({
    ...getArcadiaCalendarDetailQuery(Number(effectiveCalendarId)),
    enabled: !!effectiveCalendarId && open && activeTab === 2,
  });

  const fromDateTime = useMemo(() => dayjs().subtract(90, "day").toISOString(), []);
  const toDateTime = useMemo(() => dayjs().add(30, "day").toISOString(), []);

  const { data: calendarDatesData, isLoading: calendarDatesLoading } = useQuery({
    ...getArcadiaCalendarDatesQuery(Number(effectiveCalendarId), fromDateTime, toDateTime),
    enabled: !!effectiveCalendarId && open && activeTab === 2,
  });

  const loadTariffConfig = useCallback(async (config, type) => {
    const setResults = type === "pre" ? setPreTariffResults : setPostTariffResults;
    const setLoading = type === "pre" ? setPreTariffLoading : setPostTariffLoading;
    const setOverrides = type === "pre" ? setPreSelectedOverrides : setPostSelectedOverrides;
    const setCache = type === "pre" ? setPrePropertiesCache : setPostPropertiesCache;
    const setTotalCount = type === "pre" ? setPreTotalPropertiesCount : setPostTotalPropertiesCount;

    setLoading(true);
    try {
      const tariffId = String(config.master_tariff_id);
      const [tariffData, propertiesData] = await Promise.all([
        getTariffs(tariffId),
        getTariffsProperties(tariffId),
      ]);
      const results = Array.isArray(tariffData?.results) ? tariffData.results : [];
      const properties = Array.isArray(propertiesData?.results) ? propertiesData.results : [];
      const tariff = results.find((t) => t?.endDate == null) || results[results.length - 1];

      setTotalCount(properties.length);

      if (tariff?.lseId && properties.length > 0) {
        setCache((prev) => ({ ...prev, [tariff.lseId]: properties }));
      }

      const enrichedOverrides = (config.tariff_properties || []).map((prop) => {
        const matchingProp = properties.find((p) => p.keyName === prop.key_name);
        return {
          keyName: prop.key_name,
          displayName: matchingProp?.displayName || prop.key_name,
          value: prop.data_value,
          dataType: matchingProp?.dataType || "STRING",
          choices: matchingProp?.choices || [],
        };
      });

      setResults(results);
      setOverrides(enrichedOverrides);
    } catch (err) {
      console.error(err);
      const basicOverrides = (config.tariff_properties || []).map((prop) => ({
        keyName: prop.key_name,
        displayName: prop.key_name,
        value: prop.data_value,
        dataType: "STRING",
      }));
      setOverrides(basicOverrides);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (preConfig) {
      setPreMasterTariffId(String(preConfig.master_tariff_id || ""));
      loadTariffConfig(preConfig, "pre");
    } else {
      setPreMasterTariffId("");
      setPreTariffResults([]);
      setPreSelectedOverrides([]);
    }
    if (postConfig) {
      setPostTariffSame(false);
      setPostMasterTariffId(String(postConfig.master_tariff_id || ""));
      loadTariffConfig(postConfig, "post");
    } else {
      setPostTariffSame(true);
      setPostMasterTariffId("");
      setPostTariffResults([]);
      setPostSelectedOverrides([]);
    }
    if (arcadiaIntegration?.config?.calendar_id) {
      setSelectedCalendarId(arcadiaIntegration.config.calendar_id);
    } else {
      setSelectedCalendarId("");
    }
    setActiveTab(0);
  }, [open, preConfig, postConfig, arcadiaIntegration, loadTariffConfig]);

  const fetchTariffs = async (masterId, type) => {
    const trimmedId = `${masterId}`.trim();
    if (!trimmedId) return;
    const setResults = type === "pre" ? setPreTariffResults : setPostTariffResults;
    const setLoading = type === "pre" ? setPreTariffLoading : setPostTariffLoading;
    const setOverrides = type === "pre" ? setPreSelectedOverrides : setPostSelectedOverrides;
    const setCache = type === "pre" ? setPrePropertiesCache : setPostPropertiesCache;
    const setTotalCount = type === "pre" ? setPreTotalPropertiesCount : setPostTotalPropertiesCount;

    setLoading(true);
    setOverrides([]);
    try {
      const [tariffData, propertiesData] = await Promise.all([
        getTariffs(trimmedId),
        getTariffsProperties(trimmedId),
      ]);
      const results = Array.isArray(tariffData?.results) ? tariffData.results : [];
      const properties = Array.isArray(propertiesData?.results) ? propertiesData.results : [];
      const tariff = results.find((t) => t?.endDate == null) || results[results.length - 1];

      if (results.length === 0) {
        enqueueSnackbar("That Master Tariff ID does not exist. Try another ID.", {
          variant: "error",
          autoHideDuration: 30000,
        });
        setResults([]);
        return;
      }

      setTotalCount(properties.length);
      if (tariff?.lseId && properties.length > 0) {
        setCache((prev) => ({ ...prev, [tariff.lseId]: properties }));
      }
      setResults(results);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("That Master Tariff ID does not exist. Try another ID.", {
        variant: "error",
        autoHideDuration: 30000,
      });
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const getActiveTariff = (results) =>
    results.find((t) => t?.endDate == null) || results[results.length - 1] || null;

  const filteredProperties = (list = []) =>
    list.filter((p) =>
      ["STRING", "CHOICE", "BOOLEAN", "DATE", "DECIMAL", "INTEGER"].includes(p?.dataType),
    );

  const fetchProperties = async (masterId, type) => {
    const cache = type === "pre" ? prePropertiesCache : postPropertiesCache;
    const setCache = type === "pre" ? setPrePropertiesCache : setPostPropertiesCache;
    const setOptions = type === "pre" ? setPrePropertyOptions : setPostPropertyOptions;
    const setShow = type === "pre" ? setShowPrePropertyPicker : setShowPostPropertyPicker;
    const setLoading = type === "pre" ? setPrePropertiesLoading : setPostPropertiesLoading;
    const results = type === "pre" ? preTariffResults : postTariffResults;
    const tariff = getActiveTariff(results);

    if (tariff?.lseId && cache[tariff.lseId]) {
      setOptions(filteredProperties(cache[tariff.lseId]));
      setShow(true);
      return;
    }

    setLoading(true);
    try {
      const data = await getTariffsProperties(String(masterId));
      const props = Array.isArray(data?.results) ? data.results : [];
      if (tariff?.lseId) {
        setCache((prev) => ({ ...prev, [tariff.lseId]: props }));
      }
      setOptions(filteredProperties(props));
      setShow(true);
    } catch (err) {
      console.error(err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentSite?.uuid) return;
    setIsSaving(true);

    try {
      // Save pre-project tariff
      if (preMasterTariffId) {
        const preTariffProperties = preSelectedOverrides.map((o) => ({
          key_name: o.keyName,
          data_value: String(o.value ?? ""),
        }));
        const preData = {
          master_tariff_id: parseInt(preMasterTariffId, 10),
          lse_id: getActiveTariff(preTariffResults)?.lseId ?? null,
          bill_type: "pre_microgrid",
          tariff_properties: preTariffProperties,
          active: true,
          effective_start_date: new Date().toISOString(),
        };
        if (preConfig?.config_id) {
          await updateBillConfig(currentSite.uuid, preConfig.config_id, preData);
        } else {
          await createBillConfig(currentSite.uuid, preData);
        }
      }

      // Save post-project tariff or deactivate if same as pre
      if (!postTariffSame && postMasterTariffId) {
        const postTariffProperties = postSelectedOverrides.map((o) => ({
          key_name: o.keyName,
          data_value: String(o.value ?? ""),
        }));
        const postData = {
          master_tariff_id: parseInt(postMasterTariffId, 10),
          lse_id: getActiveTariff(postTariffResults)?.lseId ?? null,
          bill_type: "post_microgrid",
          tariff_properties: postTariffProperties,
          active: true,
          effective_start_date: new Date().toISOString(),
        };
        if (postConfig?.config_id) {
          await updateBillConfig(currentSite.uuid, postConfig.config_id, postData);
        } else {
          await createBillConfig(currentSite.uuid, postData);
        }
      } else if (postTariffSame && postConfig?.config_id) {
        await deleteBillConfig(currentSite.uuid, postConfig.config_id);
      }

      // Save billing period (calendar) integration
      if (effectiveCalendarId) {
        const selectedCal = calendars.find((c) => c.calendarId === Number(effectiveCalendarId));
        await upsertSiteIntegration(currentSite.uuid, "arcadia", {
          config: {
            lse_id: lseId || getActiveTariff(preTariffResults)?.lseId,
            calendar_id: Number(effectiveCalendarId),
            calendar_name: selectedCal?.calendarName || "",
          },
          data_types: ["billing_cycles"],
          active: true,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["bill-configs", currentSite.uuid] });
      queryClient.invalidateQueries({ queryKey: ["site-integrations", currentSite.uuid] });
      enqueueSnackbar("Integration settings saved.", {
        variant: "success",
        autoHideDuration: 3000,
      });
      onClose();
    } catch (err) {
      console.error("Failed to save integration:", err);
      enqueueSnackbar("Failed to save. Please try again.", {
        variant: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const canDeleteCurrentTab = () => {
    if (activeTab === 0) return !!preConfig?.config_id;
    if (activeTab === 1) return !!postConfig?.config_id;
    if (activeTab === 2) return !!arcadiaIntegration;
    return false;
  };

  const getDeleteLabel = () => {
    if (activeTab === 2) return "Read Cycle";
    return "Tariff";
  };

  const handleDelete = async () => {
    if (!currentSite?.uuid) return;

    setIsDeleting(true);
    try {
      if (activeTab === 0 && preConfig?.config_id) {
        await deleteBillConfig(currentSite.uuid, preConfig.config_id);
        queryClient.invalidateQueries({ queryKey: ["bill-configs", currentSite.uuid] });
      } else if (activeTab === 1 && postConfig?.config_id) {
        await deleteBillConfig(currentSite.uuid, postConfig.config_id);
        queryClient.invalidateQueries({ queryKey: ["bill-configs", currentSite.uuid] });
      } else if (activeTab === 2 && arcadiaIntegration) {
        await deleteSiteIntegration(currentSite.uuid, "arcadia");
        queryClient.invalidateQueries({ queryKey: ["site-integrations", currentSite.uuid] });
      }

      enqueueSnackbar(`${getDeleteLabel()} configuration deleted.`, {
        variant: "success",
        autoHideDuration: 3000,
      });
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error("Failed to delete configuration:", err);
      enqueueSnackbar("Failed to delete. Please try again.", {
        variant: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemoveOverride = (keyName, type) => {
    const setOverrides = type === "pre" ? setPreSelectedOverrides : setPostSelectedOverrides;
    setOverrides((prev) => prev.filter((o) => o.keyName !== keyName));
  };

  const handleOverrideValueChange = (keyName, value, type) => {
    const setOverrides = type === "pre" ? setPreSelectedOverrides : setPostSelectedOverrides;
    setOverrides((prev) => prev.map((o) => (o.keyName === keyName ? { ...o, value } : o)));
  };

  const renderOverrideRow = (prop, index, type) => {
    return (
      <Box
        key={prop.keyName}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          mb: "0.75rem",
        }}
      >
        <IconButton
          size="small"
          onClick={() => handleRemoveOverride(prop.keyName, type)}
          sx={{ color: "#006681", padding: 0 }}
        >
          <RemoveCircleOutlineOutlinedIcon fontSize="small" />
        </IconButton>
        <Typography
          sx={{
            fontSize: "14px",
            color: "rgba(0, 0, 0, 0.87)",
            width: "240px",
            flexShrink: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={prop.displayName || prop.keyName}
        >
          {prop.displayName || prop.keyName}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {prop.dataType === "BOOLEAN" && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={
                    typeof prop.value === "boolean"
                      ? prop.value
                      : prop.value === "true" || prop.value === true
                  }
                  onChange={(ev, checked) => handleOverrideValueChange(prop.keyName, checked, type)}
                />
              }
              label="True"
            />
          )}
          {prop.dataType === "DATE" && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Date"
                value={prop.value ? dayjs(prop.value) : null}
                onChange={(newValue) =>
                  handleOverrideValueChange(
                    prop.keyName,
                    newValue ? newValue.toISOString() : null,
                    type,
                  )
                }
                slotProps={{
                  textField: { InputLabelProps: { shrink: true }, size: "small" },
                }}
              />
            </LocalizationProvider>
          )}
          {prop.dataType === "CHOICE" && (
            <FormControl size="small" sx={{ width: "100%" }}>
              <InputLabel>Choose</InputLabel>
              <Select
                label="Choose"
                value={prop.value || ""}
                onChange={(ev) => handleOverrideValueChange(prop.keyName, ev.target.value, type)}
                sx={{
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                MenuProps={{ PaperProps: { style: { maxWidth: "400px" } } }}
              >
                {(prop.choices || []).map((choice) => (
                  <MenuItem
                    key={choice.value || choice.displayValue}
                    value={choice.value || choice.displayValue}
                    sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
                  >
                    {choice.displayValue || choice.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {["STRING", "DECIMAL", "INTEGER"].includes(prop.dataType) && (
            <TextField
              size="small"
              fullWidth
              value={prop.value || ""}
              onChange={(ev) => handleOverrideValueChange(prop.keyName, ev.target.value, type)}
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderTariffTab = (type) => {
    const isPost = type === "post";
    const masterTariffId = isPost ? postMasterTariffId : preMasterTariffId;
    const setMasterTariffId = isPost ? setPostMasterTariffId : setPreMasterTariffId;
    const tariffResults = isPost ? postTariffResults : preTariffResults;
    const tariffLoading = isPost ? postTariffLoading : preTariffLoading;
    const selectedOverrides = isPost ? postSelectedOverrides : preSelectedOverrides;
    const activeTariff = getActiveTariff(tariffResults);
    const existingConfig = isPost ? postConfig : preConfig;
    const totalPropertiesCount = isPost ? postTotalPropertiesCount : preTotalPropertiesCount;
    const propertiesLoading = isPost ? postPropertiesLoading : prePropertiesLoading;

    if (isPost && postTariffSame) {
      return null;
    }

    const configForCard =
      existingConfig ||
      (activeTariff
        ? {
            master_tariff_id: parseInt(masterTariffId, 10),
            tariff_properties: selectedOverrides.map((o) => ({
              key_name: o.keyName,
              data_value: String(o.value ?? ""),
            })),
          }
        : null);

    return (
      <Box>
        <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center", mb: "2rem" }}>
          <TextField
            label="Master Tariff ID"
            size="small"
            variant="outlined"
            sx={{ width: 189 }}
            value={masterTariffId}
            onChange={(ev) => setMasterTariffId(ev.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LinkOutlinedIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            color="secondary"
            disabled={!masterTariffId || tariffLoading}
            onClick={() => fetchTariffs(masterTariffId, type)}
          >
            {activeTariff ? "Update" : "Link"}
          </Button>
        </Box>

        {activeTariff && configForCard && (
          <TariffCard
            config={configForCard}
            tariffDetails={activeTariff}
            totalPropertiesCount={totalPropertiesCount}
            isLoading={tariffLoading}
          >
            {selectedOverrides.map((prop, index) => renderOverrideRow(prop, index, type))}

            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.35rem",
                color: propertiesLoading ? "rgba(0, 102, 129, 0.5)" : "#006681",
                fontSize: "14px",
                cursor: propertiesLoading ? "wait" : "pointer",
                mt: selectedOverrides.length ? "0.5rem" : 0,
                pointerEvents: propertiesLoading ? "none" : "auto",
              }}
              onClick={() => {
                if (!activeTariff?.lseId || propertiesLoading) return;
                fetchProperties(masterTariffId, type);
              }}
            >
              <AddCircleOutlineOutlinedIcon fontSize="small" />
              <Typography sx={{ fontSize: "14px", color: "#006681" }}>
                Add Property Override
              </Typography>
            </Box>
          </TariffCard>
        )}

        {tariffLoading && !activeTariff && (
          <TariffCard
            config={{ tariff_properties: [] }}
            tariffDetails={{}}
            totalPropertiesCount={0}
            isLoading
          />
        )}
      </Box>
    );
  };

  const renderBillingPeriodsTab = () => {
    const calendarDetail = calendarDetailData?.results?.[0];
    const dates = calendarDatesData?.results || [];
    const isLoading = calendarDetailLoading || calendarDatesLoading;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
        <FormControl size="small" sx={{ width: 203 }}>
          <InputLabel>Read Cycle</InputLabel>
          <Select
            value={effectiveCalendarId}
            label="Read Cycle"
            onChange={(ev) => setSelectedCalendarId(ev.target.value)}
          >
            {calendars.map((cal) => (
              <MenuItem key={cal.calendarId} value={cal.calendarId}>
                {cal.calendarName || `Calendar ${cal.calendarId}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {effectiveCalendarId && (
          <BillingPeriodsCard
            calendarDetail={calendarDetail}
            dates={dates}
            isLoading={isLoading}
          />
        )}
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ padding: "2rem" }}>
          <Box sx={{ mb: "2rem" }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Manage Integration
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Arcadia
            </Typography>
          </Box>

          <Box sx={{ mb: "2rem" }}>
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label="Tariff (Pre-Project)" sx={{ textTransform: "none" }} />
              <Tab label="Tariff (Post-Project)" sx={{ textTransform: "none" }} />
              <Tab label="Billing Periods" sx={{ textTransform: "none" }} />
            </Tabs>
            <Divider />
          </Box>

          {activeTab === 0 && renderTariffTab("pre")}

          {activeTab === 1 && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={postTariffSame}
                    onChange={(_, checked) => setPostTariffSame(checked)}
                    color="primary"
                  />
                }
                label="Post-project tariff is the same as pre-project tariff."
                sx={{
                  mb: "2rem",
                  "& .MuiFormControlLabel-label": {
                    fontSize: "16px",
                    color: "rgba(0, 0, 0, 0.87)",
                  },
                }}
              />
              {renderTariffTab("post")}
            </Box>
          )}

          {activeTab === 2 && renderBillingPeriodsTab()}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: "2rem",
            }}
          >
            {canDeleteCurrentTab() ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSaving || isDeleting}
              >
                Delete {getDeleteLabel()}
              </Button>
            ) : (
              <span />
            )}
            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              <Button variant="outlined" color="inherit" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <Box sx={{ p: 3, width: 380 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Delete {getDeleteLabel()} Configuration
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Deleting this configuration will remove it. This change applies to all users.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : `Delete ${getDeleteLabel()}`}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <PropertyPickerDialog
        open={showPrePropertyPicker}
        onClose={() => setShowPrePropertyPicker(false)}
        options={prePropertyOptions}
        onSelect={(prop) => {
          if (!prop) return;
          if (preSelectedOverrides.find((o) => o.keyName === prop.keyName)) {
            setShowPrePropertyPicker(false);
            return;
          }
          setPreSelectedOverrides((prev) => [...prev, { ...prop, value: null }]);
          setShowPrePropertyPicker(false);
        }}
      />
      <PropertyPickerDialog
        open={showPostPropertyPicker}
        onClose={() => setShowPostPropertyPicker(false)}
        options={postPropertyOptions}
        onSelect={(prop) => {
          if (!prop) return;
          if (postSelectedOverrides.find((o) => o.keyName === prop.keyName)) {
            setShowPostPropertyPicker(false);
            return;
          }
          setPostSelectedOverrides((prev) => [...prev, { ...prop, value: null }]);
          setShowPostPropertyPicker(false);
        }}
      />
    </>
  );
};

ManageArcadiaIntegrationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentSite: PropTypes.object,
  billConfigs: PropTypes.array,
  siteIntegrations: PropTypes.array,
};

export default ManageArcadiaIntegrationModal;
